import { Injectable } from "@angular/core";
import { BehaviorSubject, merge, Observable, of, ReplaySubject, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { DatabaseModel, Mote, MoteType } from '../models';

export interface EventFilter {
    key: string,
    label: string,
    fromDate: Date,
    toDate: Date,
    isEnabled: boolean,
    // Show events that has already been started before filter start time (mote end time is within range)
    showStarted: boolean,
    events: Mote[]
}

export interface EventDay {
    key: string;
    label: string;
    date: Date;
    events: Mote[];
}

export class FilterStatus {
    constructor(public msg: string, public visible: boolean) { }
}

@Injectable({ providedIn: 'root' })
export class ScheduleService {
    public showScheduleBar: boolean = false;

    private filterStatusSubject: ReplaySubject<FilterStatus> = new ReplaySubject();
    private filterList: EventFilter[] = [];
    private filterEnabledMap: { [key: string]: boolean } = {};

    // Milliseconds to show status messages before status bare is automatically hidden again
    private static readonly STATUS_TIMEOUT = 3000;

    constructor() {
    }

    /**
     * Generate a map of event Motes using the start date day as index and convert to an sorted list of 'EventDays' -
     * each entry representing all events on a specific day.
     */
    public generateEventDayList(motes: Mote[]): EventDay[] {
        // Init empty event day map
        let eventDayMap: { [key: string]: EventDay } = {};

        // Add all event motes to map
        for(let mote of motes) {
            if(mote.type != MoteType.EVENT || !mote.startTime) continue;
            // Get utc date for index in map
            let date = mote.startTime;
            // Generate unique key for map
            let day = `${date.getDate()}-${date.getDay()}-${date.getFullYear()}`;

            // Add empty eventDay to map if it doesn't exist
            if(!eventDayMap[day]) eventDayMap[day] = this.createEmptyEventDay(day, date)

            // Add event in map
            eventDayMap[day].events.push(mote);
        }

        // Convert eventDay map to array
        return DatabaseModel.mapToArray(eventDayMap).sort((dayA, dayB) => dayA.date.getTime() - dayB.date.getTime());
    }

    /**
     * Save event filters in service to filter events on the map
     */
    public setFilters(filters: EventFilter[]) {
        this.filterList = filters;
        // TODO: Notify listeners
    }

    /**
     * Create a empty Event day
     */
    private createEmptyEventDay(key: string, startDate: Date): EventDay {
        return {
            label: `${startDate.getDate()} / ${startDate.getDay()}`,
            date: startDate,
            key: key,
            events: []
        };
    }

    /**
     * Save enabled state of event filter
     */
    public updateFilterState(filter: EventFilter) {
        this.filterEnabledMap[filter.key] = filter.isEnabled;
    }

    /**
     * Get enabled state of an event filter with the given key
     */
    public isFilterEnabled(filterKey: string): boolean {
        let enabled = this.filterEnabledMap[filterKey];
        // Return state of filter or true as default if the filter is undefined
        return (enabled || enabled == undefined) ? true : false;
    }

    /**
     * Filter event motes out, according to the current user selected filter settings
     */
    public filterEvents(motes: Mote[]): Mote[] {
        // If there are no filters in the list yet, let all motes pass through
        if(this.filterList.length <= 0) return motes;

        // Filter out motes out side of selected filter times
        return motes.filter((mote) => {
            // Return true if mote start and end is within filter range
            return this.filterList.some((filter) => this.isEventWithinFilterRange(mote, filter));
        });
    }

    /**
     * Determine if a mote start time or end time is within the given filters range 
     */
    public isEventWithinFilterRange(mote: Mote, filter: EventFilter, showDisabled: boolean = false, showRangeIsWithinEvent: boolean = true): boolean {
        if(!filter.isEnabled && !showDisabled) return false;
        if(!mote.startTime) return false;

        // Check if mote startTime is within filter range
        let startIsWithinRange = (mote.startTime.getTime() >= filter.fromDate.getTime()) && (mote.startTime.getTime() <= filter.toDate.getTime());
        // Check if mote endTime is within filter range
        let endIsWithinRange = (mote.endTime.getTime() >= filter.fromDate.getTime()) && (mote.endTime.getTime() <= filter.toDate.getTime() && filter.showStarted);

        let rangeIsWithinEvent = (filter.fromDate.getTime() >= mote.startTime.getTime()) && (filter.toDate.getTime() <= mote.endTime.getTime());
        // Nice debug msg:
        //console.log("Filter event on range ("+filter.label+") \nMoteTitle:\t\t"+mote.title+"\nMoteStart:\t\t" + mote.startTime + "\nFilterStart:\t" + filter.fromDate + "\nFilterEnd:\t\t" + filter.toDate + `\nShow rangeIsWithinEvent (full): ${showRangeIsWithinEvent}\t` + "\nWithin start/end/full: \t", startIsWithinRange, endIsWithinRange, rangeIsWithinEvent);

        return (startIsWithinRange || endIsWithinRange || (rangeIsWithinEvent && showRangeIsWithinEvent));
    }

    /**
     * Send a new filter status message to listeners (show it in schedule popup)
     */
    public showStatusMsg(msg: string): void {
        this.filterStatusSubject.next(new FilterStatus(msg, true));
    }

    /**
     * Watch filter status messages like messages like filter event count status
     */
    public watchStatusMsg(): Observable<FilterStatus> {
        // Return status observable with timeout
        return this.filterStatusSubject.asObservable().pipe(switchMap((ev) => ScheduleService.watchTimeoutStatus(ev.msg)));
    }

    private static watchTimeoutStatus(status: string): Observable<FilterStatus> {
        // Create an observable that fires the status immediately and after some time the same status with show flag off  
        return merge(of(new FilterStatus(status, true)), timer(this.STATUS_TIMEOUT).pipe(map(() => new FilterStatus(status, false))));
    }
}
